import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { RestartAlt, Save, Clear, Edit, DragIndicator } from '@mui/icons-material';
import { Typography, Divider, FormControlLabel, Switch, Box, IconButton, ListItemText, ListItem, TextField } from '@mui/material';
import { Link } from '../../../components/link';
import { ExternalLink } from '../../../components/link/ExternalLink';
import { ICustomSubNavigationDetail } from '../../../models';

interface ISortableSubNavItem {
  item: ICustomSubNavigationDetail;
  navItems: ICustomSubNavigationDetail[];
  subItem: ICustomSubNavigationDetail;
  index: number;
  isNavExpanded: {
    [item: string]: boolean;
  };
  setNavExpanded: (val: { [item: string]: boolean }) => void;
  setNavItems: (navItems: ICustomSubNavigationDetail[]) => void;
  isEditing: {
    [id: string]: boolean;
  };
  originalNavItems: ICustomSubNavigationDetail[];
  setEditing: (val: { [id: number]: boolean }) => void;
}

export const SortableSubNavItem = ({
  isNavExpanded,
  setNavExpanded,
  originalNavItems,
  subItem,
  index,
  setNavItems,
  navItems,
  item,
  isEditing,
  setEditing
}: ISortableSubNavItem) => {
  const isInEditMode = isEditing[subItem.customNavigationId];
  const parentIndex = navItems.findIndex(i => i.customNavigationId === item.customNavigationId);
  const classes = useStyles();
  return (
    <Draggable key={subItem.customNavigationId} draggableId={`${subItem.customNavigationId}`} index={index} isDragDisabled={isInEditMode}>
      {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
        <div>
          <div ref={provided.innerRef} {...provided.draggableProps}>
            {isInEditMode && <Divider sx={{ marginTop: '0.5rem', marginBottom: '0.5rem' }} />}
            <ListItem sx={{ padding: '6px 0 0 18px' }}>
              {isInEditMode ? (
                <>
                  <Box
                    sx={{
                      margin: '0.5rem 0',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      marginRight: '1rem'
                    }}
                  >
                    <TextField
                      size="small"
                      label="Name"
                      autoComplete="nope"
                      variant="outlined"
                      value={subItem.name}
                      required
                      fullWidth
                      sx={{ marginBottom: '0.75rem' }}
                      onChange={e => {
                        const copy = JSON.parse(JSON.stringify(navItems));
                        copy[parentIndex].subNavigation[index].name = e.target.value;
                        setNavItems(copy);
                      }}
                      error={isInEditMode && !navItems[parentIndex].subNavigation[index].name ? true : false}
                      helperText={isInEditMode && !navItems[parentIndex].subNavigation[index].name && 'Required'}
                    />
                    <TextField
                      size="small"
                      label="URL"
                      autoComplete="nope"
                      variant="outlined"
                      value={subItem.url}
                      required
                      fullWidth
                      sx={{ marginBottom: '0.75rem' }}
                      onChange={e => {
                        const copy = JSON.parse(JSON.stringify(navItems));
                        copy[parentIndex].subNavigation[index].url = e.target.value;
                        setNavItems(copy);
                      }}
                      error={
                        // not a valid relative route
                        (!item?.openNewTab && item?.url?.startsWith('http')) ||
                        // required field for a sub route link
                        (isInEditMode && !navItems[parentIndex].subNavigation[index].url) ||
                        // they selected external link but the link doesn't start with http
                        (subItem.openNewTab && !subItem.url.startsWith('http')) ||
                        // can't contain white space
                        subItem?.url?.indexOf(' ') >= 0
                          ? true
                          : false
                      }
                      helperText={
                        isInEditMode && !navItems[parentIndex].subNavigation[index].url
                          ? 'Required'
                          : subItem.openNewTab && !subItem.url.startsWith('http')
                          ? 'Must start with http'
                          : subItem?.url?.indexOf(' ') >= 0
                          ? 'Must not contain white space'
                          : !subItem?.openNewTab && subItem?.url?.startsWith('http')
                          ? "Relative links can't start with 'http'"
                          : ''
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={subItem.openNewTab}
                          id="external"
                          onChange={e => {
                            const copy = JSON.parse(JSON.stringify(navItems));
                            copy[parentIndex].subNavigation[index].openNewTab = Boolean(e.target.checked);
                            setNavItems(copy);
                          }}
                        />
                      }
                      label="External Link?"
                    />
                  </Box>
                </>
              ) : (
                <ListItemText>
                  <Typography>{subItem.name}</Typography>
                  {subItem.openNewTab ? (
                    <ExternalLink to={subItem.url} target="_blank" className={classes.externalLink}>
                      {subItem.url}
                    </ExternalLink>
                  ) : (
                    <Link to={`/${subItem.url}`}>{subItem.url}</Link>
                  )}
                </ListItemText>
              )}
              <Box display="flex" flexDirection={isInEditMode ? 'column' : 'row'} alignSelf={isInEditMode ? 'flex-start' : 'auto'}>
                {!isInEditMode && (
                  <div {...provided.dragHandleProps}>
                    <IconButton sx={{ cursor: 'grab', p: '2px' }}>
                      <DragIndicator />
                    </IconButton>
                  </div>
                )}
                <IconButton
                  onClick={e => {
                    setEditing({
                      [subItem.customNavigationId]: !isInEditMode
                    });
                  }}
                  sx={{ p: '2px' }}
                  title={isInEditMode ? 'Save' : 'Edit'}
                  disabled={isInEditMode && (!navItems[parentIndex].subNavigation[index].name || !navItems[parentIndex].subNavigation[index].url)}
                  color={isInEditMode ? 'primary' : 'inherit'}
                >
                  {isInEditMode ? <Save /> : <Edit />}
                </IconButton>
                {isInEditMode && (
                  <IconButton
                    title="Reset"
                    color="secondary"
                    sx={{ p: '2px' }}
                    onClick={e => {
                      setNavItems(originalNavItems);
                    }}
                  >
                    <RestartAlt />
                  </IconButton>
                )}
                <IconButton
                  color="error"
                  title="Remove"
                  sx={{ p: '2px' }}
                  onClick={() => {
                    const result = window.confirm('Are you sure you want to remove this nav item?');
                    if (result) {
                      const copy = JSON.parse(JSON.stringify(navItems));

                      copy[parentIndex].subNavigation = copy[parentIndex].subNavigation.filter(l => l.customNavigationId !== subItem.customNavigationId);
                      // close the menu if they remove the last sub item
                      if (copy[parentIndex].subNavigation.length === 0) {
                        setNavExpanded({
                          ...isNavExpanded,
                          [item.name]: !isNavExpanded[item.name]
                        });
                      }
                      setNavItems(copy);
                    }
                  }}
                >
                  <Clear />
                </IconButton>
              </Box>
            </ListItem>
            {isInEditMode && <Divider sx={{ marginBottom: '0.5rem' }} />}
          </div>
          {provided.placeholder}
        </div>
      )}
    </Draggable>
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  externalLink: {
    wordBreak: 'break-word'
  }
}));
